import { Button, Icon, TextField } from "@elaniintc/goes-design-system";
import { useEntity } from "../context";
import FieldDropdown from "./FieldDropdown";
import { GripHandle } from "@/components/ui/grip-handle";
import FieldCard from "./FieldCard";
import type { FieldType } from "@/schemas/digital-files";
import { memo, useState } from "react";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getFieldTypeInfo, getModalValues } from "@/pages/digital-files/utils";
import FieldConfigModal from "../modals/FieldConfigModal";
import type { EntityField, FieldTypeName } from "@/pages/digital-files/types";
import { TextFieldConfigModal } from "../modals/TextFieldConfigModal";
import { NumericFieldConfigModal } from "../modals/NumericFieldConfigModal";
import { TextAreaFieldConfigModal } from "../modals/TextAreaFieldConfigModal";
import { DateTimeFieldConfigModal } from "../modals/DateTimeFieldConfigModal";
import { DateRangeFieldConfigModal } from "../modals/DateRangeFieldConfigModal";
import { EmailFieldConfigModal } from "../modals/EmailFieldConfigModal";

export interface Section {
  id: string;
  title: string;
  description?: string;
  fields: FieldType[];
}

interface SortableSectionRowProps {
  section: Section;
  index: number;
  isDragging: boolean;
  errors: Record<string, string>;
  updateSectionTitle: (sectionId: string, title: string) => void;
  removeSection: (sectionId: string) => void;
  removeField: (sectionId: string, fieldId: string) => void;
  handleAddField: (sectionId: string, type: FieldTypeName) => void;
  onEditField: (sectionId: string, field: FieldType) => void;
}

const SortableSectionRow = memo(
  ({
    section,
    index,
    isDragging,
    errors = {},
    updateSectionTitle,
    removeSection,
    removeField,
    handleAddField,
    onEditField,
  }: SortableSectionRowProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging: isSectionDragging,
    } = useSortable({ id: section.id });

    const style = {
      transform: transform ? CSS.Transform.toString(transform) : undefined,
      transition,
      opacity: isSectionDragging ? 0.8 : 1,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <div key={section.id} className="flex flex-col gap-2">
          <div className="flex items-center py-1.5 px-2 group">
            <span
              className="mx-2 flex items-center max-w-0 opacity-0 overflow-hidden group-hover:max-w-full group-hover:opacity-100 transition-all duration-200"
              {...listeners}
              {...attributes}
            >
              <GripHandle />
            </span>
            <div className="flex-1 flex items-center gap-3 ">
              <div className="flex-1 border-t border-neutral-quiet"></div>
              <span className="font-semibold text-sm text-on-disabled px-2 whitespace-nowrap">
                Sección {index + 1}
              </span>
              <div className="flex-1 border-t border-neutral-quiet"></div>
            </div>
            <Button
              variant="ghost"
              size="default"
              aria-label="eliminar sección"
              className="max-w-0 opacity-0 overflow-hidden group-hover:max-w-full group-hover:opacity-100 transition-all duration-200"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                removeSection(section.id);
              }}
            >
              <Icon
                type="far"
                name="trash"
                size="sm"
                className="text-on-disabled"
              />
            </Button>
          </div>
          {!isDragging && (
            <div className="px-2 py-3 flex flex-col gap-6">
              <TextField
                label="Título de la sección"
                required
                placeholder="Escribe el nombre de la sección"
                value={section.title}
                onChange={(e) => {
                  updateSectionTitle(section.id, e.target.value);
                }}
              />

              {section.fields && section.fields.length > 0 && (
                <div className="mb-4 space-y-3">
                  {section.fields.map((field) => {
                    const fieldInfo = getFieldTypeInfo(field);
                    return (
                      <FieldCard
                        key={field.id}
                        field={field}
                        fieldInfo={fieldInfo}
                        onDeleteField={(sectionId, fieldId) => {
                          removeField(sectionId, fieldId);
                        }}
                        sectionId={section.id}
                        onEditField={onEditField}
                        error={field.id ? errors[field.id] : undefined}
                      />
                    );
                  })}
                </div>
              )}

              <div>
                <FieldDropdown
                  onAdd={(fieldType) => handleAddField(section.id, fieldType)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

interface SectionListProps {
  showTrash?: boolean;
  showGrip?: boolean;
  gripProps?: React.HTMLAttributes<HTMLSpanElement>;
}

export const SectionList = ({ gripProps }: SectionListProps) => {
  const [activeFieldModalType, setActiveFieldModalType] = useState<
    FieldTypeName | "idle"
  >("idle");
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );
  const [editingField, setEditingField] = useState<EntityField | undefined>(
    undefined,
  );
  const [isDragging, setIsDragging] = useState(false);
  const {
    sections,
    fieldErrors,
    addSection,
    updateSectionTitle,
    removeSection,
    reorderSections,
    removeField,
    addField,
    updateField,
    isCreateMode,
  } = useEntity();

  const handleAddField = (sectionId: string, type: FieldTypeName) => {
    setActiveFieldModalType(type);
    setSelectedSectionId(sectionId);
    setEditingField(undefined);
  };

  const handleSaveField = (field: EntityField) => {
    if (!selectedSectionId) return;
    if (editingField) {
      updateField(selectedSectionId, editingField.id, field);
    } else {
      addField(selectedSectionId, field);
    }
    closeFieldModal();
  };

  const handleCloseModal = () => {
    closeFieldModal();
  };

  const closeFieldModal = () => {
    setSelectedSectionId(null);
    setEditingField(undefined);
    setActiveFieldModalType("idle");
  };

  const hasEmptySectionTitle = sections.some(
    (section) => section.title.trim() === "",
  );

  const moreThanOne = sections.length > 1;

  const handleEditField = (sectionId: string, field: FieldType) => {
    const modalField = getModalValues(field);
    setEditingField(modalField);
    setSelectedSectionId(sectionId);
    setActiveFieldModalType(modalField.type);
  };

  const handleRemoveField = (sectionId: string, fieldId: string) => {
    removeField(sectionId, fieldId);
  };

  const renderSections = () => {
    if (moreThanOne) {
      return (
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={(event: DragEndEvent) => {
            setIsDragging(false);
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = sections.findIndex((s) => s.id === active.id);
            const newIndex = sections.findIndex((s) => s.id === over.id);
            if (oldIndex === -1 || newIndex === -1) return;
            const newSections = arrayMove(sections, oldIndex, newIndex);
            reorderSections(newSections);
          }}
        >
          <SortableContext items={sections.map((s) => s.id)}>
            {sections.map((section, index) => (
              <SortableSectionRow
                key={section.id}
                section={section}
                index={index}
                isDragging={isDragging}
                updateSectionTitle={updateSectionTitle}
                removeSection={removeSection}
                removeField={removeField}
                handleAddField={handleAddField}
                onEditField={handleEditField}
                errors={fieldErrors}
              />
            ))}
          </SortableContext>
        </DndContext>
      );
    }

    return (
      <>
        {sections.map((section, index) => (
          <div key={section.id} className="flex flex-col gap-2">
            <div className="flex items-center py-1.5 px-2 group">
              {sections.length > 1 && (
                <span
                  className="mx-2 flex items-center max-w-0 opacity-0 overflow-hidden group-hover:max-w-full group-hover:opacity-100 transition-all duration-200"
                  {...gripProps}
                >
                  <GripHandle />
                </span>
              )}
              <div className="flex-1 flex items-center gap-3 ">
                <div className="flex-1 border-t border-neutral-quiet"></div>
                <span className="font-semibold text-sm text-on-disabled px-2 whitespace-nowrap">
                  Sección {index + 1}
                </span>
                <div className="flex-1 border-t border-neutral-quiet"></div>
              </div>
              {sections.length > 1 && (
                <Button
                  variant="ghost"
                  size="default"
                  aria-label="eliminar sección"
                  className="max-w-0 opacity-0 overflow-hidden group-hover:max-w-full group-hover:opacity-100 transition-all duration-200"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    removeSection(section.id);
                  }}
                >
                  <Icon
                    type="far"
                    name="trash"
                    size="sm"
                    className="text-on-disabled"
                  />
                </Button>
              )}
            </div>
            <div className="px-2 py-3 flex flex-col gap-6">
              <TextField
                label="Título de la sección"
                required
                placeholder="Escribe el nombre de la sección"
                value={section.title}
                onChange={(e) => {
                  updateSectionTitle(section.id, e.target.value);
                }}
              />

              {section.fields && section.fields.length > 0 && (
                <div className="mb-4 space-y-3">
                  {section.fields.map((field) => {
                    const fieldInfo = getFieldTypeInfo(field);
                    return (
                      <FieldCard
                        key={field.id}
                        field={field}
                        fieldInfo={fieldInfo}
                        sectionId={section.id}
                        onDeleteField={handleRemoveField}
                        onEditField={handleEditField}
                        error={fieldErrors[field.id]}
                      />
                    );
                  })}
                </div>
              )}

              <div>
                <FieldDropdown
                  onAdd={(fieldType) => handleAddField(section.id, fieldType)}
                />
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      {renderSections()}

      {!isDragging && (
        <div className="flex items-center min-h-10 mb-4 px-2">
          <div className="flex-1 flex items-center gap-3">
            <div className="flex-1 border-t border-neutral-quiet"></div>
            <span className="font-semibold text-sm text-on-disabled px-2 whitespace-nowrap">
              <Button
                variant="ghost"
                className="text-body-quiet"
                onClick={addSection}
                disabled={hasEmptySectionTitle}
              >
                Añadir sección
                <Icon type="fas" name="plus" />
              </Button>
            </span>
            <div className="flex-1 border-t border-neutral-quiet"></div>
          </div>
        </div>
      )}

      <TextFieldConfigModal
        key={`text-${selectedSectionId}-${editingField?.id || "new"}`}
        isCreateMode={isCreateMode}
        formData={editingField}
        isOpen={activeFieldModalType === "text"}
        onClose={closeFieldModal}
        onSave={handleSaveField}
      />
      <NumericFieldConfigModal
        key={`number-${selectedSectionId}-${editingField?.id || "new"}`}
        isCreateMode={isCreateMode}
        formData={editingField}
        isOpen={activeFieldModalType === "number"}
        onClose={closeFieldModal}
        onSave={handleSaveField}
      />
      <TextAreaFieldConfigModal
        key={`textarea-${selectedSectionId}-${editingField?.id || "new"}`}
        isCreateMode={isCreateMode}
        formData={editingField}
        isOpen={activeFieldModalType === "textarea"}
        onClose={closeFieldModal}
        onSave={handleSaveField}
      />
      <DateTimeFieldConfigModal
        key={`date-${selectedSectionId}-${editingField?.id || "new"}`}
        isCreateMode={isCreateMode}
        formData={editingField}
        isOpen={activeFieldModalType === "date"}
        onClose={closeFieldModal}
        onSave={handleSaveField}
      />
      <DateRangeFieldConfigModal
        key={`daterange-${selectedSectionId}-${editingField?.id || "new"}`}
        isCreateMode={isCreateMode}
        formData={editingField}
        isOpen={activeFieldModalType === "date_range"}
        onClose={closeFieldModal}
        onSave={handleSaveField}
      />
      <EmailFieldConfigModal
        key={`email-${selectedSectionId}-${editingField?.id || "new"}`}
        isCreateMode={isCreateMode}
        formData={editingField}
        isOpen={activeFieldModalType === "email"}
        onClose={closeFieldModal}
        onSave={handleSaveField}
      />
      <FieldConfigModal
        open={
          !!(
            activeFieldModalType &&
            [
              "phone",
              "file",
              "identification",
              "select",
              "interoperability",
            ].includes(activeFieldModalType)
          )
        }
        type={activeFieldModalType}
        isCreateMode={isCreateMode}
        sectionId={selectedSectionId}
        initialField={editingField}
        onClose={handleCloseModal}
        onSave={handleSaveField}
      />
    </>
  );
};
